import { useEffect, useRef } from "react";
import {
  useClearHomeListingPageLastScrollX,
  useHomeLastScrollX,
  useSetHomeListingPageLastScrollX,
} from "@hooks/use-home-navigation-hooks";
import ahoy from "@lib/ahoy";
import { analytics } from "@lib/trackers/analytics";
import { useSearchParams, usePathname } from "next/navigation";
import { useLocale } from "@lib/i18n/navigation-pages-router";
import { defaultLocale } from "@lib/i18n/config";
import { useCurrentUser } from "@hooks/use-current-user";
import Slider from "@components/core/Slider";
import ProductImageLink from "@components/views/Product/Shared/ProductImageLink";
import ProductLike from "@components/views/Product/Shared/ProductLike";
import ProductSalesRibbon from "@components/views/Product/Shared/ProductSalesRibbon";

function ProductSliderV2Gallery({ products = [], listingPage = {} }) {
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pathWithParams = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

  const user = useCurrentUser();
  const userGroup = user?.user_group;
  const productSliderRef = useRef();
  const listingPageLastScrollX = useHomeLastScrollX(listingPage?.slug);
  const setHomeListingPageLastScrollX = useSetHomeListingPageLastScrollX();
  const clearHomeListingPageScrollX = useClearHomeListingPageLastScrollX();
  function setSliderRef(ref) {
    productSliderRef.current = ref;
  }

  function sendTrackerData(product, index) {
    const defaultEventData = {
      tracking_name: "homeProductSlider",
      page: pathWithParams,
      listingPage: listingPage?.slug,
      position: index + 1,
      product_slug: product.slug,
      user_group: userGroup,
    };

    ahoy("$click", defaultEventData);
    analytics.track("$click", defaultEventData);
  }

  function handleClick(product, index) {
    setHomeListingPageLastScrollX(productSliderRef.current.scrollLeft, listingPage?.slug);

    sendTrackerData(product, index);
  }

  function productsList() {
    if (!products || products.length === 0) {
      return Array.from({ length: 5 }, (_, index) => (
        <section
          className="product-slider-v2-card"
          key={`skeleton-${index}`}
        >
          <div className="product-slider-v2-card-picture" />
          <div className="product-slider-v2-card-text-container">
            <div className="skeleton-text skeleton-text-title" />
            <div className="skeleton-text skeleton-text-subtitle" />
          </div>
        </section>
      ));
    }

    return products.map((product, index) => (
      <section
        className="product-slider-v2-card"
        key={product.id}
      >
        <div
          key={product.id}
          className="product-slider-v2-card-picture"
        >
          <ProductImageLink
            algolia={products.meta?.algolia}
            product={product}
            onClick={() => handleClick(product, index)}
            context={listingPage?.hub?.context}
          />
          <ProductLike
            product={product}
            className="catalog-product-like"
          />
          <ProductSalesRibbon
            product={product}
            context={listingPage?.hub?.context}
          />
        </div>
        <div className="product-slider-v2-card-text-container">
          <h6 className="mb-0 product-slider-v2-card-text">
            {product[locale === defaultLocale ? "name" : `name_${locale.toLowerCase()}`]}
          </h6>
          <p className="mb-0 text-gray product-slider-v2-card-text">{product.brand.name}</p>
        </div>
      </section>
    ));
  }

  function scrollToLastPosition() {
    const left = listingPageLastScrollX;
    productSliderRef.current?.scrollTo({ left, behavior: "smooth" });
    if (left > 0) clearHomeListingPageScrollX(listingPage?.slug);
  }

  useEffect(() => {
    if (!listingPage) return;

    scrollToLastPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingPage]);

  return (
    <div className="product-slider-v2-gallery-container">
      <Slider
        name={listingPage?.slug}
        setSliderRef={setSliderRef}
        cards={productsList()}
        productSliderRef={productSliderRef}
      />
    </div>
  );
}

export default ProductSliderV2Gallery;
